import type { QueryClient } from '@tanstack/react-query'

// ── Bhookly AI: what to refresh after a confirmed (or undone) change ───────
// A confirm card names the screens its change touches (`card.domains`, set by
// the backend's Preview). After a confirm or an undo the widget invalidates
// the React Query caches those screens read, so an open Menu tab or the
// Counter shows the change without a reload. Keys are prefix matches: 'products'
// covers ['products', 'all'] (Counter) and ['products', <category>] (Server).
//
// Add a domain here when the backend gains a write tool for it (modifiers,
// recipes, stock, suppliers, settings). An unknown domain refreshes nothing.

/** Product lists: admin Menu (products + costing tabs), Counter, Server, the pickers. */
const PRODUCT_KEYS = [
  'admin-products',
  'products',
  'variant-matrix-products',
  'tag-picker-products',
  'loyalty-eligible-picker-products',
] as const

/** Category lists: admin Menu (categories tab) and the shared useCategories cache. */
const CATEGORY_KEYS = ['admin-categories', 'categories'] as const

export const AI_ACTION_DOMAIN_QUERY_KEYS: ReadonlyMap<string, readonly string[]> = new Map<string, readonly string[]>([
  // A product write can move a category's item count (ProductForm refreshes
  // categories for the same reason).
  ['menu', [...PRODUCT_KEYS, 'categories']],
  // Product rows carry their category's name (CategoryForm refreshes products).
  ['categories', [...CATEGORY_KEYS, ...PRODUCT_KEYS]],
])

/** The query-key prefixes to refresh for these domains, de-duplicated. */
export function queryKeysForDomains(domains: readonly string[] | undefined): string[] {
  const out = new Set<string>()
  for (const d of domains ?? []) {
    for (const k of AI_ACTION_DOMAIN_QUERY_KEYS.get(d) ?? []) out.add(k)
  }
  return [...out]
}

export function invalidateActionDomains(
  qc: Pick<QueryClient, 'invalidateQueries'>,
  domains: readonly string[] | undefined,
): void {
  for (const k of queryKeysForDomains(domains)) void qc.invalidateQueries({ queryKey: [k] })
}
